import React, { useState, useEffect } from 'react';
import cn from 'classnames';
import noop from 'lodash/noop';
import styles from './toggle.module.scss';

interface IProps {
  /**
   * Optional CSS class name for custom styling of the toggle
   */
  className?: string,
  /**
   * The name attribute for the toggle input
   */
  name?: string,
  /**
   * The unique identifier for the toggle input
   */
  id?: string,
  /**
   * The type of the toggle input (default is "checkbox")
   */
  type?: string,
  /**
   * Whether the toggle is checked or not
   */
  checked?: boolean,
  /**
   * Whether the toggle is disabled, preventing user interaction
   */
  disabled?: boolean,
  /**
   * Focus event handler for the toggle
   */
  onFocus?: (e: React.FocusEvent) => void,
  /**
   * Blur event handler for the toggle
   */
  onBlur?: (e: React.FocusEvent) => void,
  /**
   * Change event handler for the toggle (fires when value changes)
   */
  onChange: () => void,
  /**
   * Click event handler for the toggle
   */
  onClick?: (e: React.MouseEvent) => void,
  /**
   * The tab index for keyboard navigation
   */
  tabIndex?: number,
  /**
   * Whether the toggle is read-only (disables editing but still interactive)
   */
  readOnly?: boolean,
  /**
   * Unique identifier for testing purposes
   */
  dataTest?: string,
}

const Toggle = (props: IProps) => {
  const {
    name,
    id,
    type = 'checkbox',
    disabled,
    readOnly,
    tabIndex = 0,
    onClick = noop,
    onFocus = noop,
    onChange = noop,
    onBlur = noop,
    dataTest,
  } = props;

  const [checked, setChecked] = useState(!!props.checked);

  useEffect(() => {
    setChecked(!!props.checked);
  }, [props.checked]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) {
      return;
    }
    setChecked(event.target.checked);
    onChange();
  };

  return (
    <div
      className={ cn(styles.wrp, props.className) }
      data-test={ dataTest }
    >
      <label
        htmlFor={ name }
        className={ styles.switch }
      >
        <input
          type={ type }
          id={ id }
          name={ name }
          readOnly={ readOnly }
          disabled={ disabled }
          tabIndex={ tabIndex }
          checked={ checked }
          onClick={ onClick }
          onFocus={ onFocus }
          onBlur={ onBlur }
          onChange={ handleChange }
        />
        <span className={ cn(styles.slider, styles.round) } />
      </label>
    </div>
  );
};

export default Toggle;
